// SPDX-License-Identifier: Apache-2.0 AND LicenseRef-Pachamama-Proprietary
// Copyright 2026 The Zyra Project
// Modifications copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * Dataset loading — fetching, displaying info, and wiring up image/video datasets.
 *
 * Extracted from InteractiveSphere to isolate data-loading concerns.
 */

import { HLSService, type VideoProxyFile, type VideoProxyResponse } from './hlsService'
import { isZipDownloadable } from './downloadService'
import type { Dataset, AppState, GlobeRenderer, VideoTextureHandle } from '../types'
import { overlayOptionsFromDataset } from './datasetOverlayOptions'
import { addedDay, formatDate, isSubDailyPeriod, inferDisplayInterval } from '../utils/time'
import { logger } from '../utils/logger'
import { escapeHtml, escapeAttr } from '../ui/domUtils'
import { closeActiveChat } from '../ayni-chat/activeChat'
import type { PlaybackState } from '../ui/playbackController'
import { updatePlayButton, loadCaptions } from '../ui/playbackController'
import { startDwell, type DwellHandle } from '../analytics'
import { addViewSeconds } from './visitMemory'
import { recommendRelated, normalizeTitle as normalizeRelatedTitle } from './relatedDatasets'
import { openDatasetMenu } from '../ui/datasetMenu'
import { openDownloadDialog } from '../ui/downloadDialogUI'
import { t, tAttr } from '../i18n'
import type { DsaAttribution } from './dsaMetadata'
import { reviewedBudget } from './privateData'
import { streamAuthFetch, StreamRefusedError } from './streamToken'
import { accuracySection } from '../ui/accuracySection'
import { onFidelityLoaded } from './valueFidelity'
import { calibrateRelease } from './decoderRange'

/** Tier B dwell handle for the info panel — non-null while the
 * panel is expanded (collapsed = user can't read the body so it
 * doesn't count). One handle per displayed dataset; rebuilt on
 * dataset change. Tier-gated at emit time, wiring is unconditional. */
let infoPanelDwellHandle: DwellHandle | null = null
let infoPanelDwellDatasetId: string | null = null

/**
 * Stop the active info-panel dwell handle and credit its elapsed
 * (visibility-paused) time to visit memory's `viewSeconds` for the
 * dataset it was tracking. Phase 7 §9.2 piggybacks on the dwell
 * handle's lifecycle rather than running a parallel timer, so the
 * "only count while the document is visible" semantics come for free
 * (dwell pauses on tab-hide). Idempotent — a null handle is a no-op.
 *
 * Note: the dwell helper's own `pagehide` drain bypasses this wrapper,
 * so a session that ends with the panel still expanded loses that
 * final open segment's `viewSeconds`. That's an acceptable rounding
 * loss for a convenience cache; every collapse / dataset-change /
 * panel-rebuild commits cleanly.
 */
function commitInfoPanelDwell(): void {
  if (!infoPanelDwellHandle) return
  if (infoPanelDwellDatasetId) {
    addViewSeconds(infoPanelDwellDatasetId, infoPanelDwellHandle.elapsed() / 1000)
  }
  infoPanelDwellHandle.stop()
  infoPanelDwellHandle = null
  infoPanelDwellDatasetId = null
}

// --- Dataset loader constants ---
const DESCRIPTION_MAX_LENGTH = 600
const DESCRIPTION_MIN_CUT = 200
const VIDEO_LOAD_TIMEOUT_MS = 20000
const FIRST_FRAME_FALLBACK_MS = 150
/** `HTMLMediaElement.HAVE_CURRENT_DATA` — the readyState at which the
 * frame at the current position is decoded, which is all a
 * `VideoTexture` needs. Upstream waits for `canplay` (readyState 3,
 * HAVE_FUTURE_DATA), which a DASH stream on Quest/Chromium can defer
 * past the timeout while it already holds a perfectly good first frame. */
const HAVE_CURRENT_DATA = 2
const SCRUBBER_MAX = '1000'
/** Bytes of an MPD response inspected for the `<MPD` root element.
 * The XML prolog plus a long comment fits well inside this, and the
 * preflight never needs the whole manifest. */
const DASH_PREFLIGHT_BYTES = 512

/** Callbacks the dataset loader uses to communicate with the main app. */
export interface DatasetLoaderCallbacks {
  showPlaybackControls: (show: boolean) => void
  showTimeLabel: (show: boolean) => void
}

/**
 * Options controlling which singular UI affordances a load call is
 * allowed to touch. Non-primary panel loads pass `isPrimary: false`
 * so they don't clobber the scrubber, mute button, playback controls,
 * time label, or appState fields that belong to the primary panel.
 */
export interface DatasetLoaderOptions {
  /** Whether this load is targeting the primary viewport. Default `true`. */
  isPrimary?: boolean
}

// --- Image loading ---

/** Load an image dataset onto the globe. */
export async function loadImageDataset(
  dataset: Dataset,
  renderer: GlobeRenderer,
  appState: AppState,
  isMobile: boolean,
  callbacks: DatasetLoaderCallbacks,
  options: DatasetLoaderOptions = {},
): Promise<HTMLImageElement> {
  const isPrimary = options.isPrimary ?? true

  const img = await loadImage(dataset.dataLink)

  renderer.updateTexture(img, overlayOptionsFromDataset(dataset))

  // Only the primary drives the singular time label and playback UI.
  if (isPrimary) {
    if (dataset.startTime) {
      const showTime = isSubDailyPeriod(dataset.period)
      appState.timeLabel = formatDate(new Date(dataset.startTime), showTime)
      callbacks.showTimeLabel(true)
    } else {
      callbacks.showTimeLabel(false)
    }
    callbacks.showPlaybackControls(false)
  }

  logger.info(`[App] Image dataset loaded successfully: ${img.src}`)
  return img
}

/**
 * Load one image. AYNI (2026-10-07): every picture is one file — a NOAA
 * library object on catalog.pachamama-studios.stream, or a stream host
 * frame — so TerraViz's `_4096` / `_2048` / `_1024` suffix ladder for
 * NOAA's CloudFront and S3 copies is gone.
 */
function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error(`Failed to load image ${url}`))
    img.src = url
  })
}

// --- Video loading ---

/** Load a video dataset via HLS streaming, set up the video texture, and configure playback controls. */
/**
 * Choose a directly-playable file from a manifest's `files[]`.
 *
 * Preference order is a Vimeo-shaped ladder first, then anything with a
 * link at all. That last clause is the fix: the previous fallback
 * required `f.width && f.link`, and `width` is optional on
 * `VideoProxyFile` — so a single-file manifest, whose one entry the
 * backend emits as `{ quality: 'source', size: 0, type, link }` with no
 * dimensions, matched none of the three branches and raised "No
 * playable video source found" while holding a perfectly good URL.
 *
 * A `link` is the only field playing it actually requires.
 */
export function pickDirectFile(files: VideoProxyFile[]): VideoProxyFile | undefined {
  return files.find(f => f.quality === '1080p' && f.link)
    ?? files.find(f => f.quality === '720p' && f.link)
    ?? files.find(f => f.width && f.link)
    ?? files.find(f => f.link)
}

/**
 * Fail fast when a DASH URL does not actually resolve to an MPD.
 *
 * dash.js reports a broken manifest as a generic playback error several
 * seconds later, on an element whose `readyState` never leaves 0 — which
 * looks exactly like a slow network in the UI. One GET turns the two
 * deployment mistakes that produce it into a message naming the fix: an
 * R2 bucket without CORS, and a Vite dev server answering a relative
 * `/realtime/...` path with the app's own HTML (a 200, so `res.ok` alone
 * would pass it).
 */
async function assertDashManifestReachable(dashUrl: string): Promise<void> {
  let res: Response
  try {
    // AYNI: through streamAuthFetch, so an own dataset's MPD carries its token (privateData.ts) and a stream's the stream token (streamToken.ts).
    res = await streamAuthFetch(dashUrl, {
      headers: { Accept: 'application/dash+xml, application/xml, text/xml, */*' },
    })
  } catch (err) {
    throw new Error(
      `Unable to fetch DASH MPD ${dashUrl}. If this is a Cloudflare R2 URL, check CORS and VITE_REALTIME_DASH_BASE_URL. ` +
      `${err instanceof Error ? err.message : String(err)}`,
    )
  }

  // AYNI (S-02): a refusal is told by streamAuthFetch (sign in, or the plans); not a load error on top.
  if (res.status === 401) throw new StreamRefusedError(dashUrl)
  if (!res.ok) {
    throw new Error(`DASH MPD fetch failed for ${dashUrl}: ${res.status} ${res.statusText}.`)
  }

  const text = await res.text()
  if (!/<MPD[\s>]/i.test(text.slice(0, DASH_PREFLIGHT_BYTES))) {
    throw new Error(`DASH MPD fetch for ${dashUrl} did not return an MPD document.`)
  }
}

/**
 * Wait until the media element has at least one decodable frame.
 *
 * DASH streams on Quest/Chromium can parse the MPD and initialize MSE without
 * firing `canplay` until playback is started. Because our video elements are
 * muted for autoplay compliance, a temporary play nudge is safe and gives
 * dash.js a reason to pull the first segment. HLS/direct streams benefit from
 * the broader event set too: `loadeddata` is sufficient for a VideoTexture even
 * if `canplay` is delayed by buffering heuristics.
 */
async function waitForVideoFrameReady(video: HTMLVideoElement): Promise<void> {
  if (video.readyState >= HAVE_CURRENT_DATA) return

  let shouldPauseAfterNudge = false
  const wasPaused = video.paused

  const playPromise = video.play()
  if (playPromise) {
    shouldPauseAfterNudge = wasPaused
    playPromise.catch(() => {
      // Autoplay can still be blocked by the UA. The event listeners below
      // remain active, and the timeout reports the actionable failure.
    })
  }

  try {
    await new Promise<void>((resolve, reject) => {
      let timeoutId: ReturnType<typeof setTimeout> | null = null

      const cleanup = () => {
        if (timeoutId) clearTimeout(timeoutId)
        video.removeEventListener('loadeddata', onReady)
        video.removeEventListener('canplay', onReady)
        video.removeEventListener('canplaythrough', onReady)
        video.removeEventListener('playing', onReady)
        video.removeEventListener('timeupdate', onReady)
        video.removeEventListener('error', onError)
      }

      const finish = () => {
        cleanup()
        resolve()
      }

      const onReady = () => {
        if (video.readyState >= HAVE_CURRENT_DATA) finish()
      }

      const onError = () => {
        cleanup()
        reject(new Error('Video failed while loading the first frame'))
      }

      if (video.readyState >= HAVE_CURRENT_DATA) {
        finish()
        return
      }

      video.addEventListener('loadeddata', onReady)
      video.addEventListener('canplay', onReady)
      video.addEventListener('canplaythrough', onReady)
      video.addEventListener('playing', onReady)
      video.addEventListener('timeupdate', onReady)
      video.addEventListener('error', onError, { once: true })
      timeoutId = setTimeout(() => {
        cleanup()
        reject(new Error('Video took too long to load — check your connection and try again'))
      }, VIDEO_LOAD_TIMEOUT_MS)
    })
  } finally {
    if (shouldPauseAfterNudge) video.pause()
  }
}

/**
 * Force the first frame to decode before the texture is attached to the
 * sphere, so the globe never shows a black cap while the decoder spins up.
 *
 * The timeout is armed on *every* path, not only when
 * `requestVideoFrameCallback` is missing: a muted autoplay-blocked element can
 * accept `play()` and still never present a frame, and the upstream shape
 * waited on that callback forever. Pausing only when the element started
 * paused keeps a caller that deliberately handed us a playing stream playing.
 */
async function primeFirstVideoFrame(video: HTMLVideoElement): Promise<void> {
  const wasPaused = video.paused
  try {
    await video.play()
    await new Promise<void>((resolve) => {
      let done = false
      const finish = () => {
        if (done) return
        done = true
        resolve()
      }
      if ('requestVideoFrameCallback' in video) {
        ;(video as HTMLVideoElement & {
          requestVideoFrameCallback: (callback: () => void) => number
        }).requestVideoFrameCallback(() => finish())
      }
      setTimeout(finish, FIRST_FRAME_FALLBACK_MS)
    })
  } catch {
    // Autoplay blocked — texture will update when user presses play.
  } finally {
    if (wasPaused) video.pause()
  }
}

/**
 * AYNI (Quest review A11): a stream that carries no values (no colour scale,
 * no release encoding), so a smaller rendition shows the same thing. Data
 * streams keep their full rendition: the probe and the tools read it.
 */
export function isPictureStream(dataset: Pick<Dataset, 'colorScale' | 'releaseEncoding'>): boolean {
  return !dataset.colorScale && !dataset.releaseEncoding
}

export async function loadVideoDataset(
  dataset: Dataset,
  renderer: GlobeRenderer,
  appState: AppState,
  isMobile: boolean,
  playbackState: PlaybackState,
  callbacks: DatasetLoaderCallbacks,
  options: DatasetLoaderOptions = {},
): Promise<{ hlsService: HLSService; videoTexture: VideoTextureHandle; streamKind: 'hls' | 'dash' | 'direct' }> {
  const isPrimary = options.isPrimary ?? true
  const hlsService = new HLSService()
  const video = hlsService.createVideo()
  // Which transport served this dataset. Starts at `direct` because the
  // single-file-manifest paths below are progressive by
  // construction; the HLS and DASH branches overwrite it.
  let streamKind: 'hls' | 'dash' | 'direct' = 'direct'

  let manifest: VideoProxyResponse
  // A realtime/forecast dataset carries its MPD URL straight in
  // `dataLink` (see `fetchRealtimeDashDatasets`), and `format` is
  // the authoritative marker — the extension test catches rows whose
  // format was not normalised. A direct `.m3u8` is played as HLS.
  // Everything else is one MP4 file: AYNI (2026-10-07), a NOAA library
  // video on catalog.pachamama-studios.stream; the Vimeo manifests
  // fetched through the Zyra Project's proxy went with the S3 catalog.
  if (dataset.format === 'application/dash+xml' || /\.mpd(\?|#|$)/i.test(dataset.dataLink)) {
    manifest = {
      id: dataset.id,
      title: dataset.title,
      duration: 0,
      dash: dataset.dataLink,
      files: [],
    }
  } else if (/\.m3u8(\?|#|$)/i.test(dataset.dataLink)) {
    manifest = {
      id: dataset.id,
      title: dataset.title,
      duration: 0,
      hls: dataset.dataLink,
      files: [],
    }
  } else {
    manifest = {
      id: dataset.id,
      title: dataset.title,
      duration: 0,
      files: [{ quality: 'source', size: 0, type: 'video/mp4', link: dataset.dataLink }],
    }
  }
  logger.info('[App] Video manifest received:', { duration: manifest.duration, qualities: manifest.files.length })

  // An empty `hls` is a *choice*, not a failure. The manifest route
  // emits one for a single-file MP4 reference — `url:<href>` and a
  // non-`.m3u8` `r2:<key>` both land in `externalVideoManifest` — and
  // its comment says the frontend "picks up `files[0].link`" instead.
  // That contract was never honoured here: progressive was reachable
  // only by throwing through the HLS path, which cost three separate
  // things. hls.js treats an empty source as a fatal network error and
  // the handler below *retries* it before rejecting, so a designed
  // path burned retries and called `reportError('hls', …)` on every
  // load. Worse, Safari answers `canPlayType('…mpegurl')` truthy, so
  // an empty URL took the native branch as `video.src = ''`, which is
  // not guaranteed to fire `error` — a load that may never settle
  // either way, on the platform that most needs this path.
  // A `dash` URL is the third case, and it must not land in the
  // single-file branch either — so the progressive path is gated on
  // *both* stream URLs being absent, not on whether `files[]` is
  // populated: a realtime MPD manifest legitimately carries none.
  if (!manifest.hls && !manifest.dash) {
    const direct = pickDirectFile(manifest.files)
    if (!direct) throw new Error('Manifest declared no HLS stream and no playable file')
    logger.info('[App] Manifest is single-file; loading progressive MP4 directly')
    await hlsService.loadDirect(direct.link, video)
  } else {
    const hlsUrl = manifest.hls
    const dashUrl = manifest.dash
    try {
      if (hlsUrl) {
        streamKind = 'hls'
        await hlsService.loadStream(hlsUrl, video, isMobile, isPictureStream(dataset))
      } else if (dashUrl) {
        // Preflight before dash.js: a manifest that 404s, or that a
        // Vite dev server answers with the app's HTML, otherwise
        // surfaces as a generic playback error seconds later.
        streamKind = 'dash'
        await assertDashManifestReachable(dashUrl)
        await hlsService.loadDash(dashUrl, video, isMobile, isPictureStream(dataset))
      } else {
        // Unreachable while the guard above holds; kept so the two
        // cannot drift apart silently.
        throw new Error('No HLS or DASH stream URL in manifest')
      }
    } catch (streamError) {
      const mp4File = pickDirectFile(manifest.files)
      if (!mp4File) {
        // Surface the stream error rather than a generic "no source"
        // message. For a DASH row it carries the MPD URL, the status
        // and the `VITE_REALTIME_DASH_BASE_URL` hint that names the
        // actual fix.
        logger.warn('[App] Stream load failed and no direct MP4 fallback is available:', streamError)
        throw streamError instanceof Error ? streamError : new Error('Stream load failed')
      }
      logger.warn('[App] Stream load failed, falling back to direct MP4:', streamError)
      streamKind = 'direct'
      await hlsService.loadDirect(mp4File.link, video)
    }
  }

  await waitForVideoFrameReady(video)

  // Infer display interval from time range + video duration.
  // Only the primary panel's load drives the shared playback state.
  // Pass `period` and `frames.count` so the cadence matches the
  // imagery rather than the ms-per-frame estimate — fixes the
  // climate-dataset label-crawl bug (Plan §5.1).
  if (isPrimary) {
    if (dataset.startTime && dataset.endTime) {
      const start = new Date(dataset.startTime)
      const end = new Date(dataset.endTime)
      playbackState.displayInterval = inferDisplayInterval(start, end, video.duration, {
        period: dataset.period,
        frameCount: dataset.frames?.count,
      })
      logger.info('[App] Inferred display interval:', playbackState.displayInterval)
    } else {
      playbackState.displayInterval = null
    }
  }

  // Force first frame decode before attaching to the sphere.
  await primeFirstVideoFrame(video)
  // AYNI: a release's codes as this browser's decoder delivers them, read
  // off the frame's calibration strip before anything draws or reads it.
  if (dataset.releaseEncoding) dataset.releaseEncoding = calibrateRelease(video, dataset.releaseEncoding)
  video.currentTime = 0

  // Show mute button only when the stream has audio. Only the primary
  // drives the singular mute button visibility — non-primary videos
  // share the same mute control, so skip if we're not primary.
  // (The primary's hls service always wins the button.)
  if (isPrimary) {
    const muteBtn = document.getElementById('mute-btn') as HTMLElement | null
    if (muteBtn) {
      muteBtn.style.display = hlsService.hasAudio ? '' : 'none'
    }
  }

  const videoTexture = renderer.setVideoTexture(video, overlayOptionsFromDataset(dataset))
  videoTexture.needsUpdate = true

  // Scrubber + playback transport are singular — primary only.
  if (isPrimary) {
    const scrubber = document.getElementById('scrubber') as HTMLInputElement
    if (scrubber) {
      scrubber.max = SCRUBBER_MAX
      scrubber.value = '0'
    }

    callbacks.showPlaybackControls(true)
    updatePlayButton(true)

    if (dataset.closedCaptionLink) {
      loadCaptions(video, dataset.closedCaptionLink, playbackState)
    }
  }

  logger.info('[App] Video dataset loaded, duration:', video.duration, 's')
  return { hlsService, videoTexture, streamKind }
}

// --- Dataset info panel ---

/**
 * Trim a description to the panel's collapsed-state cap, snapping
 * to the last sentence boundary so the ellipsis lands at a clean
 * stopping point. The full text remains available for the expand
 * affordance.
 */
function truncateDescription(text: string): string {
  if (text.length <= DESCRIPTION_MAX_LENGTH) return text
  const cut = text.lastIndexOf('.', DESCRIPTION_MAX_LENGTH)
  return text.substring(0, cut > DESCRIPTION_MIN_CUT ? cut + 1 : DESCRIPTION_MAX_LENGTH) + '…'
}

/**
 * Render one row of the Credits section. The value is wrapped in
 * an external link when `affiliationUrl` is present so the
 * affiliation institution is reachable in one click — the SOS
 * catalog itself does the same.
 */
function renderCreditRow(label: string, value: string, affiliationUrl?: string): string {
  const valueHtml = affiliationUrl
    ? `<a href="${escapeAttr(affiliationUrl)}" target="_blank" rel="noopener noreferrer">${escapeHtml(value)}</a>`
    : escapeHtml(value)
  return `<div class="info-credit-row">`
    + `<dt class="info-credit-label">${escapeHtml(label)}</dt>`
    + `<dd class="info-credit-value">${valueHtml}</dd>`
    + `</div>`
}

/** Only web links leave the panel; anything else in a descriptor is dropped. */
function webUrl(url: string | null): string | null {
  return url && /^https?:\/\//i.test(url) ? url : null
}

/**
 * AYNI — show the credit a stream's publisher asks to be given with its
 * data (the .dsa's `attribution`), with links to the source and the
 * licence. For CC BY data the credit is a condition of the licence, so
 * it needs somewhere a viewer can reach. Fills the slot
 * `displayDatasetInfo` leaves; a no-op when the panel has moved on to
 * another dataset.
 */
/**
 * AYNI: fill (or refill) the info panel's Accuracy section for a dataset
 * whose release carries an error budget; called again once a release is
 * resolved after the panel was drawn.
 */
export function fillAccuracySlot(dataset: Dataset): void {
  const slot = document.querySelector<HTMLElement>('#info-body .info-accuracy-slot')
  if (!slot) return
  const section = accuracySection(dataset.releaseEncoding, dataset.ownData ? reviewedBudget(dataset.id) : null, accuracyFrameClock?.() ?? null)
  slot.replaceChildren(...(section ? [section] : []))
  // AYNI: the host's diagnostics (the codec setting) may land after the panel is drawn: redraw once they do.
  accuracyRefill?.()
  const diag = dataset.releaseEncoding?.fidelity?.diagnosticsUrl
  accuracyRefill = diag
    ? onFidelityLoaded((url) => {
        if (url !== diag) return
        accuracyRefill?.()
        accuracyRefill = null
        const now = document.querySelector<HTMLElement>('#info-body .info-accuracy-slot')
        if (now !== slot || !slot.isConnected) return
        const again = accuracySection(dataset.releaseEncoding, dataset.ownData ? reviewedBudget(dataset.id) : null, accuracyFrameClock?.() ?? null)
        slot.replaceChildren(...(again ? [again] : []))
      })
    : null
}

/** The pending redraw of the Accuracy section, if its release's diagnostics have not arrived yet. */
let accuracyRefill: (() => void) | null = null

/** AYNI: the time of the frame on screen, for the Accuracy section's "This frame" row (set by the app; value error audit, 2026-10-19). */
let accuracyFrameClock: (() => number | null) | null = null
export function setAccuracyFrameClock(clock: (() => number | null) | null): void {
  accuracyFrameClock = clock
}

export function renderStreamCredit(datasetId: string, credit: DsaAttribution): void {
  const slot = document.querySelector<HTMLElement>('#info-body .info-stream-credit')
  if (!slot || slot.getAttribute('data-dataset-id') !== datasetId) return
  const links: string[] = []
  const seen = new Set<string>()
  const link = (url: string | null, label: string | null) => {
    const href = webUrl(url)
    if (!href || !label || seen.has(href)) return
    seen.add(href)
    links.push(`<a href="${escapeAttr(href)}" target="_blank" rel="noopener noreferrer" class="info-catalog-link">${escapeHtml(label)} \u2197</a>`)
  }
  for (const source of credit.sources) {
    link(source.sourcePage, source.label)
    link(source.licenseUrl, source.licenseName)
  }
  slot.innerHTML =
    `<p class="info-section-label">${escapeHtml(t('infoPanel.section.dataCredit'))}</p>`
    + `<p class="info-stream-credit-text">${escapeHtml(credit.text)}</p>`
    + (links.length ? `<p class="info-stream-credit-links">${links.join(' ')}</p>` : '')
}

/**
 * Build the related-datasets section. Combines the manually-curated
 * `EnrichedMetadata.relatedDatasets` (rendered first, in author
 * order) with algorithmic recommendations filling in up to the §4.2
 * cap. Returns an empty string when nothing surfaces.
 *
 * The algorithmic portion is the lexical scorer from
 * `relatedDatasets.ts` (word matching over titles, keywords and
 * categories). AYNI: TerraViz's semantic "more like this" upgrade —
 * `/api/v1/datasets/:id/related` over Vectorize — went with the
 * catalog node (2026-10-06).
 *
 * Manual entries that don't resolve to a catalog row render as
 * grayed-out text (off-catalog references — preserved from the
 * pre-§4.2 behaviour so a curator's notes about external context
 * still show). Algorithmic recommendations always resolve, so they
 * always render as live links.
 */
function renderRelatedDatasetsHtml(
  target: Dataset,
  datasets: Dataset[],
): string {
  const manual = target.enriched?.relatedDatasets ?? []
  const manualLinks: Array<{ label: string; match: Dataset | null }> = manual.map((rd) => {
    const wanted = normalizeRelatedTitle(rd.title)
    const match = datasets.find((d) => normalizeRelatedTitle(d.title) === wanted) ?? null
    return { label: rd.title, match }
  })

  const manualIds = new Set<string>()
  const manualTitles = new Set<string>()
  for (const entry of manualLinks) {
    if (entry.match) manualIds.add(entry.match.id)
    manualTitles.add(normalizeRelatedTitle(entry.label))
  }

  const algorithmic = recommendRelated(target, datasets, manualIds, manualTitles)

  if (manualLinks.length === 0 && algorithmic.length === 0) return ''

  let html = `<div class="info-related-section">`
  html += `<p class="info-section-label">${escapeHtml(t('infoPanel.relatedDatasets'))}</p>`
  html += `<ul class="info-related">`
  for (const entry of manualLinks) {
    if (entry.match) {
      html += `<li><a href="?dataset=${encodeURIComponent(entry.match.id)}" data-dataset-id="${escapeAttr(entry.match.id)}">${escapeHtml(entry.label)}</a></li>`
    } else {
      html += `<li><span class="info-related-offcatalog">${escapeHtml(entry.label)}</span></li>`
    }
  }
  for (const candidate of algorithmic) {
    html += `<li><a href="?dataset=${encodeURIComponent(candidate.id)}" data-dataset-id="${escapeAttr(candidate.id)}">${escapeHtml(candidate.title)}</a></li>`
  }
  html += `</ul></div>`
  return html
}

/**
 * Wire related-dataset links within `scope` to load in-place. The URL
 * update preserves any existing `?catalog=true` flag (Phase 1 §3.2) so
 * a related-link click while in catalog mode keeps the catalog↔sphere
 * tab control visible — same contract as `selectDatasetFromBrowse` in
 * main.ts.
 */
function wireRelatedLinks(scope: ParentNode, onLoadDataset: (id: string) => void): void {
  scope.querySelectorAll('a[data-dataset-id]').forEach(link => {
    link.addEventListener('click', (ev) => {
      ev.preventDefault()
      const id = (link as HTMLElement).dataset.datasetId
      if (id) {
        const params = new URLSearchParams(window.location.search)
        params.set('dataset', id)
        window.history.pushState({}, '', `?${params.toString()}`)
        onLoadDataset(id)
      }
    })
  })
}

/** Populate and display the dataset info panel with metadata, legend and related datasets. */
export function displayDatasetInfo(
  dataset: Dataset,
  datasets: Dataset[],
  onLoadDataset: (id: string) => void,
): void {
  const infoPanel = document.getElementById('info-panel')
  const infoTitle = document.getElementById('info-title')
  const infoBody = document.getElementById('info-body')
  const infoHeader = document.getElementById('info-header')
  if (!infoPanel || !infoTitle || !infoBody || !infoHeader) return

  // If the panel is showing a different dataset from the one whose
  // dwell we're currently tracking, close out the previous dwell
  // before re-rendering. The new dataset's dwell starts fresh on
  // the next expand click.
  if (infoPanelDwellHandle && infoPanelDwellDatasetId !== dataset.id) {
    commitInfoPanelDwell()
  }

  const e = dataset.enriched

  infoTitle.textContent = dataset.title

  let html = ''

  // Top-of-panel source line — the organisation that owns / produced
  // the data, as a labelled row. Datasets where only `organization`
  // is populated still surface it; datasets where neither is set
  // omit the row entirely.
  if (dataset.organization) {
    html += `<p class="info-source">${escapeHtml(dataset.organization)}</p>`
  }
  // AYNI — own data: private to the account (and its lab); no Khipu,
  // download or related datasets, which are for the catalogue.
  const own = dataset.ownData === true
  if (own) html += `<p class="info-own-data">${escapeHtml(t('myData.info.private'))}</p>`

  // AYNI (owner, 2026-11-03: playlists retire for Khipus): "Add to a
  // Khipu", right under the source line so it shows without scrolling. It
  // opens the dataset menu under itself; `data-dataset-id` says which.
  if (!own) {
    html += `<button type="button" class="info-action-btn info-add-to-khipu"`
      + ` data-dataset-id="${escapeAttr(dataset.id)}" aria-haspopup="menu" aria-expanded="false"`
      + ` aria-label="${escapeAttr(t('datasetMenu.info.aria', { title: dataset.title }))}">`
      + escapeHtml(t('datasetMenu.info.label'))
      + `</button>`
  }

  // "Download as .zip" affordance — §8.2. Same data-attribute idiom
  // as the Khipu button, so a future refactor can lift the row into a
  // shared component. `isZipDownloadable` additionally
  // suppresses the button on datasets we know will fail today
  // (plain HLS-only videos post Phase 3 r2-hls migration) — widen
  // the check once issues #147 / #148 land.
  if (!own && isZipDownloadable(dataset)) {
    html += `<button type="button" class="info-action-btn info-zip-download"`
      + ` data-dataset-id="${escapeAttr(dataset.id)}"`
      + ` aria-label="${escapeAttr(t('zip.action.zipDownload.aria', { title: dataset.title }))}">`
      + escapeHtml(t('zip.action.zipDownload'))
      + `</button>`
  }

  // Description with show-more/show-less. The collapsed form is the
  // same 600-char snippet the panel has rendered for years; the
  // expanded form is the full text, scrollable inside the panel
  // body. `data-collapsed`/`data-full` carry the two variants so
  // the toggle handler can swap without re-rendering from source.
  const description = e?.description || dataset.abstractTxt
  if (description) {
    const collapsed = truncateDescription(description)
    const isTruncated = collapsed !== description
    if (isTruncated) {
      html += `<div class="info-description-wrap" data-truncated="true">`
      html += `<p class="info-description"`
        + ` data-collapsed="${escapeAttr(collapsed)}"`
        + ` data-full="${escapeAttr(description)}">${escapeHtml(collapsed)}</p>`
      html += `<button type="button" class="info-description-toggle" aria-expanded="false">${escapeHtml(t('infoPanel.description.showMore'))}</button>`
      html += `</div>`
    } else {
      html += `<p class="info-description">${escapeHtml(description)}</p>`
    }
  }

  if (dataset.legendLink) {
    html += `<img src="${dataset.legendLink}" alt="${escapeAttr(dataset.title)} legend" class="info-legend-thumb" tabindex="0" role="button" aria-label="Enlarge ${escapeAttr(dataset.title)} legend">`
  }

  if (e?.categories) {
    const cats = Object.entries(e.categories)
      .map(([group, subs]) => subs.length ? `${escapeHtml(group)}: ${subs.map(escapeHtml).join(', ')}` : escapeHtml(group))
    html += `<p class="info-categories">${cats.join(' · ')}</p>`
  }

  const keywords = e?.keywords || dataset.tags
  if (keywords && keywords.length > 0) {
    html += `<div class="info-keywords">`
    keywords.forEach(kw => {
      html += `<span class="info-keyword">${escapeHtml(kw)}</span>`
    })
    html += `</div>`
  }

  // "Captions available" indicator — disambiguates "this dataset
  // has no captions" from "captions failed to load" (Plan §5.2).
  // The CC button on the transport only appears after a successful
  // SRT fetch; this badge surfaces the *intent* unconditionally.
  if (dataset.closedCaptionLink) {
    html += `<p class="info-captions-badge">`
    html += `<span class="info-captions-badge-glyph" aria-hidden="true">CC</span>`
    html += escapeHtml(t('infoPanel.captionsAvailable'))
    html += `</p>`
  }

  // --- Credits section — Phase 2 §4.1 -----------------------------
  // Each row is independently conditional; the section header only
  // renders if at least one row will follow it. Affiliation URLs
  // wrap the name in an external link when present.
  const creditRows: string[] = []
  if (e?.datasetDeveloper?.name) {
    creditRows.push(renderCreditRow(
      t('infoPanel.developedBy'),
      e.datasetDeveloper.name,
      e.datasetDeveloper.affiliationUrl,
    ))
  }
  if (e?.visDeveloper?.name) {
    creditRows.push(renderCreditRow(
      t('infoPanel.visualizationBy'),
      e.visDeveloper.name,
      e.visDeveloper.affiliationUrl,
    ))
  }
  if (e?.dateAdded) {
    creditRows.push(renderCreditRow(t('infoPanel.dateAdded'), addedDay(e.dateAdded)))
  }
  if (creditRows.length > 0) {
    html += `<p class="info-section-label">${escapeHtml(t('infoPanel.section.credits'))}</p>`
    html += `<dl class="info-credits">${creditRows.join('')}</dl>`
  }

  // --- AYNI: the data credit a stream's publisher asks for. Its .dsa
  // arrives after the panel is drawn, so `renderStreamCredit` fills this
  // slot then; it stays empty for a dataset with no such credit. --------
  html += `<div class="info-stream-credit" data-dataset-id="${escapeAttr(dataset.id)}"></div>`
  // AYNI: the release's measured error budget (accuracySection.ts), filled below.
  html += `<div class="info-accuracy-slot"></div>`
  // AYNI: "Cite this view" for every dataset (research tier, slice 1);
  // main.ts answers the click, since the view lives there.
  html += `<p class="info-cite"><button type="button" class="info-cite-btn">${escapeHtml(t('cite.open'))}</button></p>`

  // --- Related datasets — manual entries first, then algorithmic
  // recommendations to fill the list up to the §4.2 cap. ----------
  const relatedHtml = own ? '' : renderRelatedDatasetsHtml(dataset, datasets)
  if (relatedHtml) html += relatedHtml

  // --- Thumbnail + download link — Phase 2 §4.3 -------------------
  // Only when a separate thumbnail asset exists. The download link
  // is a plain `<a download>`.
  if (dataset.thumbnailLink) {
    html += `<a href="${escapeAttr(dataset.thumbnailLink)}"`
      + ` download`
      + ` target="_blank" rel="noopener noreferrer"`
      + ` class="info-thumbnail-download"`
      + ` aria-label="${tAttr('infoPanel.thumbnail.download')}">`
    html += `<img src="${escapeAttr(dataset.thumbnailLink)}"`
      + ` alt="${escapeAttr(t('infoPanel.thumbnail.alt', { title: dataset.title }))}"`
      + ` class="info-thumbnail">`
    html += `<span class="info-thumbnail-download-label">${escapeHtml(t('infoPanel.thumbnail.download'))}</span>`
    html += `</a>`
  }

  // --- External catalog link --------------------------------------
  if (e?.catalogUrl) {
    html += `<p class="info-section-label">${escapeHtml(t('infoPanel.section.links'))}</p>`
    html += `<a href="${escapeAttr(e.catalogUrl)}" target="_blank" rel="noopener noreferrer" class="info-catalog-link">${escapeHtml(t('infoPanel.catalogLink'))}</a>`
  }

  infoBody.innerHTML = html
  infoPanel.classList.remove('hidden')
  fillAccuracySlot(dataset)

  // Wire "Add to a Khipu" to open the dataset menu (`datasetMenu.ts`)
  // under the button itself: the Khipus, and Share. The menu handles its
  // own outside-click / Escape close.
  const addBtn = infoBody.querySelector<HTMLButtonElement>('.info-add-to-khipu')
  if (addBtn) {
    addBtn.addEventListener('click', (ev) => {
      ev.stopPropagation()
      const id = addBtn.dataset.datasetId
      if (id) openDatasetMenu(id, addBtn)
    })
  }

  // Wire the info-panel zip-download button (web only) to open the
  // §8.2 download dialog. The dialog itself is opener-anchored so
  // re-clicking the button doesn't accidentally re-close.
  const zipBtn = infoBody.querySelector<HTMLButtonElement>('.info-zip-download')
  if (zipBtn) {
    zipBtn.addEventListener('click', (ev) => {
      ev.stopPropagation()
      const id = zipBtn.dataset.datasetId
      if (id) void openDownloadDialog(id, zipBtn)
    })
  }

  // Wire up legend thumbnail to open modal
  const legendThumb = infoBody.querySelector('.info-legend-thumb') as HTMLElement | null
  if (legendThumb && dataset.legendLink) {
    const openLegendModal = () => {
      let overlay = document.getElementById('legend-modal-overlay')
      if (!overlay) {
        overlay = document.createElement('div')
        overlay.id = 'legend-modal-overlay'
        overlay.className = 'legend-modal-overlay'
        overlay.setAttribute('role', 'dialog')
        overlay.setAttribute('aria-modal', 'true')
        overlay.setAttribute('aria-label', 'Legend')
        overlay.innerHTML = `<img class="legend-modal-img" alt="Legend"><button class="legend-modal-close" aria-label="Close legend">&times;</button>`
        const closeModal = () => {
          overlay!.classList.add('hidden')
          legendThumb.focus()
        }
        overlay.querySelector('.legend-modal-close')!.addEventListener('click', (e) => {
          e.stopPropagation()
          closeModal()
        })
        overlay.addEventListener('click', closeModal)
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && !overlay!.classList.contains('hidden')) closeModal()
        })
        document.body.appendChild(overlay)
      }
      const img = overlay.querySelector('img')!
      img.src = dataset.legendLink!
      img.alt = `${dataset.title} legend`
      overlay.setAttribute('aria-label', `${dataset.title} legend`)
      overlay.classList.remove('hidden')
      // Move focus into the modal
      const closeBtn = overlay.querySelector('.legend-modal-close') as HTMLElement
      if (closeBtn) closeBtn.focus()
    }
    legendThumb.addEventListener('click', openLegendModal)
    legendThumb.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openLegendModal() }
    })
  }

  // Wire up related dataset links to load in-place.
  wireRelatedLinks(infoBody, onLoadDataset)

  // Wire up the description show-more / show-less toggle.
  const descWrap = infoBody.querySelector('.info-description-wrap[data-truncated="true"]') as HTMLElement | null
  if (descWrap) {
    const descEl = descWrap.querySelector('.info-description') as HTMLElement | null
    const toggleBtn = descWrap.querySelector('.info-description-toggle') as HTMLButtonElement | null
    if (descEl && toggleBtn) {
      const collapsed = descEl.dataset.collapsed ?? ''
      const full = descEl.dataset.full ?? ''
      toggleBtn.addEventListener('click', () => {
        const expanded = toggleBtn.getAttribute('aria-expanded') === 'true'
        if (expanded) {
          descEl.textContent = collapsed
          toggleBtn.setAttribute('aria-expanded', 'false')
          toggleBtn.textContent = t('infoPanel.description.showMore')
        } else {
          descEl.textContent = full
          toggleBtn.setAttribute('aria-expanded', 'true')
          toggleBtn.textContent = t('infoPanel.description.showLess')
        }
      })
    }
  }

  // Toggle expand/collapse on header click or keyboard
  const toggleInfoPanel = () => {
    const expanded = infoPanel.classList.toggle('expanded')
    infoHeader.setAttribute('aria-expanded', String(expanded))
    // Close chat when expanding info — both can't be tall at the same time
    if (expanded) closeActiveChat()
    // Tier B dwell — track time the panel is actually expanded
    // (collapsed reduces it to a one-line header, no body
    // reading is possible). Targets `dataset:<id>` so dashboards
    // can split per-dataset reading time without reaching for the
    // session-scoped layer_loaded join.
    if (expanded) {
      if (infoPanelDwellHandle) commitInfoPanelDwell()
      infoPanelDwellHandle = startDwell(`dataset:${dataset.id}`)
      infoPanelDwellDatasetId = dataset.id
      // §9.2 — the visit itself is recorded at dataset-load time (see
      // main.ts displayDataset). Expanding the info panel only accrues
      // viewSeconds, committed when the dwell handle closes.
    } else if (infoPanelDwellHandle) {
      commitInfoPanelDwell()
    }
  }
  infoHeader.onclick = toggleInfoPanel
  infoHeader.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      toggleInfoPanel()
    }
  })
}
